import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { UserPlus, Search, AlertTriangle } from 'lucide-react';
import { useUserSearch } from './useUserSearch';

export const InviteManagerDialog = ({
  open,
  onOpenChange,
  inviteEmail,
  onInviteEmailChange,
  onSend,
  sending,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searching,
    showResults,
    reset,
  } = useUserSearch();

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next) {
      reset();
      onInviteEmailChange('');
    }
  };

  const selectUser = (user) => {
    onInviteEmailChange(user.email);
    setSearchQuery(user.display_name);
    // Hide the dropdown without clearing the input
    reset();
    setSearchQuery(user.display_name);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="invite-manager-button">
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Manager
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite Manager</DialogTitle>
          <DialogDescription>
            Search for your manager by name or email address. Type at least 3 characters to search.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2 relative">
            <Label htmlFor="manager-search">Search by Name or Email</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="manager-search"
                placeholder="Type at least 3 characters..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  onInviteEmailChange('');
                }}
                className="pl-9"
                data-testid="manager-search-input"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="w-full mt-2 bg-white border-2 border-primary rounded-md shadow-lg max-h-48 overflow-auto">
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => selectUser(user)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-100 border-b last:border-b-0 transition-colors"
                    data-testid={`search-result-${user.id}`}
                  >
                    <div className="font-medium text-sm">{user.display_name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                  </button>
                ))}
              </div>
            )}

            {showResults && searchResults.length === 0 && searchQuery.length >= 3 && !searching && (
              <div className="text-sm text-muted-foreground mt-2 p-3 bg-yellow-50 rounded-md border border-yellow-200">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-yellow-600" />
                No users found matching "{searchQuery}". You can still enter an email manually below.
              </div>
            )}

            {searching && (
              <div className="text-sm text-muted-foreground mt-2 p-3 bg-blue-50 rounded-md">
                Searching...
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="manager-email">
              Manager Email {inviteEmail && <span className="text-emerald-600 text-xs">(selected)</span>}
            </Label>
            <Input
              id="manager-email"
              type="email"
              placeholder="manager@therapymgmt.com"
              value={inviteEmail}
              onChange={(e) => onInviteEmailChange(e.target.value)}
              data-testid="manager-email-input"
            />
            <p className="text-xs text-muted-foreground">
              Double-check the email address is correct. Invitations expire after 2 days if not accepted.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSend}
            disabled={sending || !inviteEmail.trim()}
            data-testid="send-invite-button"
          >
            {sending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
